import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { formatDate } from '@/shared/format';
import type { ProviderSummary } from '@/api/schemas/ai';

/**
 * The credential STATUS of a provider.
 *
 * ---------------------------------------------------------------------------
 * NO CREDENTIAL VALUE IS EVER RENDERED
 * ---------------------------------------------------------------------------
 * The API never returns one, and this component has no path that could show
 * one even if it did. What it shows is the status an operator actually needs:
 * whether a key is configured, which key version is live, and when it was
 * last rotated.
 *
 * The write-only input that SETS a credential lives on the rotation panel,
 * not here. This panel is read-only by construction.
 */
export function CredentialStatus({ provider }: { provider: ProviderSummary }): ReactNode {
  const configured = provider.credentialConfigured;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={configured ? 'positive' : 'caution'}>
          {configured ? 'Configured' : 'Not configured'}
        </Badge>
        <span className="text-sm text-content-muted">
          {configured
            ? 'The stored key is never displayed, copied or returned by the API.'
            : 'This provider has no key yet, so calls to it will be refused.'}
        </span>
      </div>

      <dl className="grid gap-3 sm:grid-cols-2">
        <StatusField
          label="Key version"
          value={provider.credentialKeyVersion === null ? '—' : `Version ${provider.credentialKeyVersion}`}
        />
        <StatusField
          label="Last rotated"
          value={provider.credentialRotatedAt === null ? '—' : formatDate(provider.credentialRotatedAt)}
        />
      </dl>
    </div>
  );
}

function StatusField({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
      <dd className="text-sm text-content">{value}</dd>
    </div>
  );
}

/**
 * The same status, in one line, for a table cell.
 *
 * Configured or not, the key version, and when it was last rotated — the three
 * facts a list reader needs — and nothing else. There is still no path by
 * which a credential value could reach this component.
 */
export function CredentialSummaryLine({ provider }: { provider: ProviderSummary }): ReactNode {
  return (
    <div className="flex flex-col">
      <Badge tone={provider.credentialConfigured ? 'positive' : 'caution'}>
        {provider.credentialConfigured ? 'Configured' : 'Not configured'}
      </Badge>
      <span className="mt-1 text-xs text-content-muted">
        {provider.credentialKeyVersion === null
          ? 'No key version'
          : `Key version ${provider.credentialKeyVersion}`}
      </span>
      <span className="text-xs text-content-muted">
        {provider.credentialRotatedAt === null
          ? 'Never rotated'
          : `Rotated ${formatDate(provider.credentialRotatedAt)}`}
      </span>
    </div>
  );
}
